<template>
  <div class="payment-simple">
    <div v-if="errorMessage">{{ errorMessage }}</div>

    <div v-if="!useNewPaymentMethod && paymentMethod">
      <p>We will charge your {{ paymentMethod.brand }} credit card on file ending in
        <strong>{{ paymentMethod.last4 }}</strong></p>
      <a @click.prevent="useNewPaymentMethod = true" href="#">Use a different credit card</a>
    </div>
    <form v-if="shouldShowPaymentForm" id="payment-form" @submit.prevent="pay">
      <p v-if="useNewPaymentMethod">
        <a @click.prevent="useNewPaymentMethod = false" href="#">Use my saved credit card details</a>
      </p>
      <div id="payment-element">
        <!-- Elements will create form elements here -->
      </div>
      <div id="error-message" v-if="errorMessage" class="alert alert-danger" style="margin-top: 20px;">
        {{ errorMessage }}
      </div>
    </form>

    <div style="text-align: right" v-if="isLoggedIn && (useNewPaymentMethod || !paymentMethod)">
      <div class="form-check" style="margin-top: 20px;">
        <input
            type="checkbox"
            v-model="updateAccount"
            class="form-check-input"
            id="update_account"
            name="update_account"
            required
        />
        <label class="form-check-label" for="update_account">Save this credit card for faster future
          checkouts</label>
      </div>
    </div>

    <div style="text-align: right" v-if="!isLoggedIn">
      <div class="form-check" style="margin-top: 20px;">
        <input
            type="checkbox"
            v-model="createAccount"
            class="form-check-input"
            id="create_account"
            name="create_account"
            required
        />
        <label class="form-check-label" for="create_account">Save my details for faster future checkouts</label>
      </div>
    </div>

    <div style="text-align: right" v-if="createAccount">
      <div class="form-check" style="margin-top: 20px;">
        <input
            type="password"
            v-model="password"
            class="form-control"
            id="password"
            name="password"
            style="width: auto;display: inline-block"
            placeholder="Password"
            required
        />
      </div>
    </div>

    <div style="text-align: right">
      <div class="form-check" style="margin-top: 20px;">
        <input
            type="checkbox"
            v-model="termsAccepted"
            class="form-check-input"
            id="terms_accepted"
            name="terms_accepted"
            required
        />
        <label class="form-check-label" for="terms_accepted">I accept the</label>
        <a target="_blank" href="/terms">terms of service</a>
      </div>
    </div>

    <loading-button
        style="margin-top: 32px;margin-left: auto;"
        :loading="isLoading"
        :text="`Pay ${price} ${currency}`"
        @clicked="pay"
    ></loading-button>
  </div>
</template>

<script>
import {useUserStore} from "../stores/user";
import {storeToRefs} from "pinia";
import {useOrderStore} from "../stores/order";

export default {
  setup() {
    const orderStore = useOrderStore()
    const {getIntent} = orderStore
    const {uuid} = storeToRefs(orderStore)

    const userStore = useUserStore()
    const {isLoggedIn, paymentMethod, email} = storeToRefs(userStore)
    userStore.fetchMe();

    return {
      email,
      isLoggedIn,
      paymentMethod,
      uuid,
      createAccountFromOrder: userStore.createAccountFromOrder,
      updateAccountFromOrder: userStore.updateAccountFromOrder,
      orderId: uuid,
      getIntent,
    }
  },

  props: {
    clientSecret: String,
    currency: String,
    total: Number,
    returnUrl: String,
    onPay: {
      type: Function,
      default: () => null,
    },
  },

  mounted: function () {
    this.stripe = Stripe(window.stripe_pk);
    if (this.shouldShowPaymentForm) {
      this.setupStripeElements();
    }
  },

  data: () => ({
    stripe: null,
    elements: null,
    isLoading: false,
    transactionId: null,
    paymentStatus: false,
    termsAccepted: false,
    dropoffAccepted: false,
    errorMessage: "",
    createAccount: false,
    updateAccount: false,
    useNewPaymentMethod: false,
    accountCreated: false,
    accountUpdated: false,
    password: null,
  }),

  methods: {
    pay: async function (e) {
      e.preventDefault();

      this.accountCreated = false;

      if (!this.termsAccepted) {
        alert('Please accept the terms of service before completing your order.');
        return;
      }

      this.isLoading = true;

      await this.onPay();

      this.errorMessage = "";

      let result = null;

      await this.getIntent();

      if (this.createAccount) {
        try {
          await this.createAccountFromOrder(this.orderId, this.password);
          this.accountCreated = true;
        } catch (error) {
          alert(error?.response?.data?.message);
          this.isLoading = false;
          return;
        }
      } else if (this.updateAccount) {
        try {
          await this.updateAccountFromOrder(this.orderId);
          this.accountUpdated = true;
        } catch (error) {
          alert(error?.response?.data?.message);
          this.isLoading = false;
          return;
        }
      }

      const redirectUrlSuffix = this.accountCreated ? '?account_created=true' : '';

      if (this.paymentMethod && !this.useNewPaymentMethod) {
        result = await this.stripe.confirmCardPayment(this.clientSecret, {
          return_url: this.returnUrl + redirectUrlSuffix,
          receipt_email: this.email,
          payment_method: this.paymentMethod.payment_method,
        });

        if (result.error) {
          this.errorMessage = result.error.message;
          this.isLoading = false;
          return;
        } else {
          window.location.href = this.returnUrl;
          return;
        }
      } else {
        result = await this.stripe.confirmPayment({
          //`Elements` instance that was used to create the Payment Element
          elements: this.elements,
          // clientSecret: this.clientSecret,
          confirmParams: {
            return_url: this.returnUrl + redirectUrlSuffix,
            receipt_email: this.email,
          },
        });
      }

      if (result?.error) {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Show error to your customer (for example, payment
        // details incomplete)
        this.errorMessage = result.error.message;
      } else {
        // Your customer will be redirected to your `return_url`. For some payment
        // methods likeen iDEAL, your customer will be redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.
        this.$emit('update:payment-status', true);
      }

      this.isLoading = false;
    },
    clearPaymentMethod: function () {
      this.paymentMethod = null;
    },
    setupStripeElements: function () {
      this.$nextTick(() => {
        const options = {
          clientSecret: this.clientSecret,
          // Fully customizable with appearance API.
        };

        this.elements = this.stripe.elements(options);

        const paymentElement = this.elements.create('payment');
        paymentElement.mount('#payment-element');
      })
    }
  },
  computed: {
    price: function () {
      return this.$options.filters.formattedPrice(this.total, this.currency);
    },
    shouldShowPaymentForm: function () {
      return this.useNewPaymentMethod || !this.paymentMethod;
    },
  },
  watch: {
    shouldShowPaymentForm: {
      handler: function (val) {
        this.errorMessage = "";
        if (val) {
          this.setupStripeElements();
        }
      },
      deep: true
    },
  }
}
</script>
